import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { AuthLayout } from "@/components/AuthLayout";
import { useAuth } from "@/context/AuthContext";
import { api, errorMessage } from "@/lib/api";
import { bankIdLoginUrl } from "@/lib/bankid";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Loader2, Users, Fingerprint, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const ROLE_LABEL = { innsyn: "innsyn i", handtering: "håndtering av" };

export default function RegisterPage() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const inviteToken = params.get("invite") || "";
  const [invite, setInvite] = useState(null); // { email, owner_name, role, message, account_exists }
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    password: "",
    confirm: "",
  });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [bankid, setBankid] = useState(null);

  useEffect(() => {
    api
      .get("/auth/bankid/status", { skipActAs: true })
      .then((r) => setBankid(r.data))
      .catch(() => setBankid({ enabled: false }));
  }, []);

  useEffect(() => {
    if (!inviteToken) return;
    api
      .get(`/delegations/invite/${encodeURIComponent(inviteToken)}`, {
        skipActAs: true,
      })
      .then((r) => {
        setInvite(r.data);
        setForm((f) => ({ ...f, email: r.data.email || f.email }));
      })
      .catch(() => setInvite({ invalid: true }));
  }, [inviteToken]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.password.length < 8)
      return setError("Passordet må ha minst 8 tegn");
    if (form.password !== form.confirm)
      return setError("Passordene er ikke like");
    setBusy(true);
    try {
      // Invitert via fullmakt? Ta brukeren rett til Familie og fullmakt for å svare (leses av PublicOnly i App.js).
      if (invited) sessionStorage.setItem("rp_post_auth_redirect", "/familie");
      const { me, result } = await register({
        full_name: form.full_name.trim(),
        email: form.email.trim(),
        password: form.password,
        invite_token: invited ? inviteToken : null,
      });
      if (result?.email_verification === "sendt") {
        toast.info(`Vi har sendt en bekreftelseslenke til ${form.email.trim()}`, {
          description: result.unlinked_invitations > 0 ? "Invitasjoner til fullmakt kobles til kontoen din når adressen er bekreftet." : "Klikk på lenken i e-posten for å bekrefte adressen. Du får også en kom-i-gang-veiledning på e-post – den ligger alltid under Hjelp.",
          duration: 9000,
        });
      } else {
        toast.info("Velkommen til Controlle!", { description: "Brukerveiledningen for alle modulene finner du under Hjelp i menyen.", duration: 8000 });
      }
      navigate(me?.pending_invitations > 0 ? "/familie" : "/", {
        replace: true,
      });
    } catch (err) {
      sessionStorage.removeItem("rp_post_auth_redirect");
      setError(errorMessage(err, "Kunne ikke opprette konto"));
    } finally {
      setBusy(false);
    }
  };

  const invited = invite && !invite.invalid;
  const bankidEnabled = !!bankid?.enabled;
  const bankidOnly = bankidEnabled && !!bankid?.only_for_users;

  const startBankId = () => {
    if (inviteToken) sessionStorage.setItem("rp_invite_token", inviteToken);
    window.location.assign(
      bankIdLoginUrl({ invite: inviteToken || undefined }),
    );
  };

  const bankidCta = bankidEnabled ? (
    <div className="space-y-3" data-testid="register-bankid-section">
      <Button
        type="button"
        className="w-full h-12 text-base"
        onClick={startBankId}
        data-testid="register-bankid-button"
      >
        <Fingerprint className="h-5 w-5 mr-2" /> Opprett konto med BankID
      </Button>
      <p className="text-xs text-muted-foreground flex items-start gap-1.5">
        <ShieldCheck className="h-3.5 w-3.5 mt-0.5 shrink-0 text-primary" />{" "}
        Navnet ditt hentes fra BankID – du oppgir bare e-postadressen din.
        Fødselsnummer lagres ikke i klartekst.
      </p>
    </div>
  ) : null;

  return (
    <AuthLayout
      title={invited ? "Du er invitert til Controlle" : "Opprett konto"}
      subtitle={
        invited
          ? `Opprett konto med ${invite.email}, så ligger invitasjonen klar til deg.`
          : bankidEnabled
            ? "Kom i gang på under ett minutt – identiteten din bekreftes trygt med BankID."
            : "Kom i gang på under ett minutt. BankID-innlogging aktiveres når administrator har lagt inn avtalen."
      }
      footer={
        <p>
          Har du allerede konto?{" "}
          <Link
            to={
              inviteToken
                ? `/logg-inn?invite=${encodeURIComponent(inviteToken)}`
                : "/logg-inn"
            }
            className="text-primary font-medium hover:underline"
            data-testid="go-to-login-link"
          >
            Logg inn
          </Link>
        </p>
      }
    >
      {invited ? (
        <Alert
          className="mb-5 border-primary/40 bg-accent/40"
          data-testid="register-invite-banner"
        >
          <Users className="h-4 w-4" />
          <AlertTitle>{invite.owner_name} har invitert deg</AlertTitle>
          <AlertDescription>
            {invite.owner_name} ønsker å gi deg{" "}
            {ROLE_LABEL[invite.role] || invite.role} regningene sine. Du
            bestemmer selv om du vil godta etter at kontoen er opprettet.
            {invite.message ? (
              <span className="block mt-1 italic">«{invite.message}»</span>
            ) : null}
            {invite.account_exists ? (
              <span className="block mt-1">
                Det finnes allerede en konto med denne adressen –{" "}
                <Link to="/logg-inn" className="underline font-medium">
                  logg inn
                </Link>{" "}
                for å svare på invitasjonen.
              </span>
            ) : null}
          </AlertDescription>
        </Alert>
      ) : invite?.invalid ? (
        <Alert className="mb-5" data-testid="register-invite-invalid">
          <AlertTitle>Invitasjonslenken er ikke gyldig lenger</AlertTitle>
          <AlertDescription>
            Du kan likevel opprette konto. Be personen som inviterte deg om å
            sende en ny invitasjon hvis den mangler etterpå.
          </AlertDescription>
        </Alert>
      ) : null}
      {bankidCta}
      {bankidOnly ? (
        <p
          className="text-xs text-muted-foreground text-center mt-5"
          data-testid="register-bankid-only-hint"
        >
          Kontoer i Controlle opprettes med BankID. Administratorer logger inn med
          e-post og passord.
        </p>
      ) : (
        <>
          {bankidEnabled ? (
            <div className="relative py-4">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t" />
              </div>
              <div className="relative flex justify-center text-xs">
                <span className="bg-card px-2 text-muted-foreground">
                  eller med e-post og passord
                </span>
              </div>
            </div>
          ) : null}
          <form
            onSubmit={submit}
            className="space-y-5"
            noValidate
            data-testid="register-password-form"
          >
            <div className="space-y-2">
              <Label htmlFor="full_name">Fullt navn</Label>
              <Input
                id="full_name"
                value={form.full_name}
                onChange={set("full_name")}
                required
                className="h-11"
                autoComplete="name"
                data-testid="register-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">E-postadresse</Label>
              <Input
                id="email"
                type="email"
                value={form.email}
                onChange={set("email")}
                required
                className="h-11"
                autoComplete="email"
                data-testid="register-email-input"
              />
              {invited &&
              form.email.trim().toLowerCase() !==
                (invite.email || "").toLowerCase() ? (
                <p
                  className="text-xs text-warn"
                  data-testid="register-invite-email-hint"
                >
                  Invitasjonen gjelder {invite.email}. Bruker du en annen
                  adresse, kobles ikke invitasjonen til kontoen.
                </p>
              ) : null}
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="password">Passord</Label>
                <Input
                  id="password"
                  type="password"
                  value={form.password}
                  onChange={set("password")}
                  required
                  className="h-11"
                  autoComplete="new-password"
                  data-testid="register-password-input"
                />
                <p className="text-xs text-muted-foreground">Minst 8 tegn.</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirm">Gjenta passord</Label>
                <Input
                  id="confirm"
                  type="password"
                  value={form.confirm}
                  onChange={set("confirm")}
                  required
                  className="h-11"
                  autoComplete="new-password"
                  data-testid="register-confirm-input"
                />
              </div>
            </div>
            {error ? (
              <p
                className="text-sm text-destructive"
                role="alert"
                data-testid="register-error"
              >
                {error}
              </p>
            ) : null}
            <Button
              type="submit"
              className="w-full h-11"
              disabled={busy}
              data-testid="register-submit-button"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}{" "}
              Opprett konto
            </Button>
            <p className="text-xs text-muted-foreground text-center">
              Ved å fortsette godtar du vilkår og personvernerklæring.
            </p>
          </form>
        </>
      )}
    </AuthLayout>
  );
}
